import { Checkbox } from "@/components/ui/checkbox";
import { Clock, AlertTriangle, ChevronDown, ChevronUp, CopyPlus, Repeat, StickyNote, Truck, UserPlus } from "lucide-react";
import { PRIORITIES } from "@/lib/colors";
import { isOverdue } from "@/lib/dates";

const Details = ({ task, p, late, meta }) => (
  <>
    <span className="mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[10.5px] leading-tight text-slate-500">
      {task.time && (
        <span className="inline-flex items-center gap-0.5 font-mono tabular-nums">
          <Clock className="h-2.5 w-2.5" />
          {task.time}
        </span>
      )}
      {task.vehicle && (
        <span data-testid={`task-vehicle-${task.id}`} className="inline-flex items-start gap-0.5 font-medium text-slate-600">
          <Truck className="mt-px h-2.5 w-2.5 shrink-0" />
          <span className="break-words">{task.vehicle}</span>
        </span>
      )}
      {task.trailer && (
        <span data-testid={`task-trailer-${task.id}`} className="whitespace-nowrap rounded bg-slate-100 px-1 font-semibold text-slate-600">+Rimorchio</span>
      )}
      <span className="inline-flex items-center gap-0.5">
        <span className="h-1.5 w-1.5 rounded-full" style={{ background: p.color }} />
        {p.label}
      </span>
      {late && (
        <span data-testid={`task-late-${task.id}`} className="late-badge">
          <AlertTriangle className="h-2.5 w-2.5" /> In ritardo
        </span>
      )}
      {meta}
    </span>
    {task.notes?.trim() && (
      <span data-testid={`task-notes-${task.id}`} className="task-notes mt-1 flex items-start gap-1">
        <StickyNote className="mt-px h-2.5 w-2.5 shrink-0" />
        <span className="min-w-0 whitespace-pre-wrap break-words">{task.notes.trim()}</span>
      </span>
    )}
  </>
);

const ReorderArrows = ({ reorder, testId }) => (
  <div className="reorder-arrows">
    <button type="button" data-testid={`${testId}-up`} disabled={!reorder.up} onClick={reorder.up || undefined} aria-label="Sposta su">
      <ChevronUp className="h-4 w-4" />
    </button>
    <button type="button" data-testid={`${testId}-down`} disabled={!reorder.down} onClick={reorder.down || undefined} aria-label="Sposta giù">
      <ChevronDown className="h-4 w-4" />
    </button>
  </div>
);

export const TaskCard = ({ task, h, meta, footer, reorder, showAssign = false, assignLabel = "Assegna", idPrefix = "" }) => {
  const late = isOverdue(task);
  const p = PRIORITIES[task.priority] || PRIORITIES.media;
  const onDragStart = (e) => {
    e.dataTransfer.setData("text/plain", task.id);
    e.dataTransfer.effectAllowed = "copyMove";
  };

  return (
    <div
      data-testid={`${idPrefix}task-card-${task.id}`}
      lang="it"
      draggable
      onDragStart={onDragStart}
      className={`task-card ${task.completed ? "is-done" : ""} ${late ? "is-late" : ""}`}
      style={{ borderLeftColor: late ? "#F43F5E" : p.color }}
    >
      <div className="relative flex items-start gap-1.5">
        {task.recurring ? (
          <Repeat data-testid={`${idPrefix}task-recurring-${task.id}`} className="mt-px h-4 w-4 shrink-0 text-violet-500" aria-label="Ricorrente" />
        ) : (
          <Checkbox
            data-testid={`${idPrefix}task-checkbox-${task.id}`}
            checked={task.completed}
            onCheckedChange={() => h.toggle(task)}
            aria-label="Segna come completata"
            className="mt-px h-4 w-4 shrink-0 rounded-[4px]"
          />
        )}
        <button type="button" data-testid={`${idPrefix}task-open-${task.id}`} onClick={() => h.edit(task)} className={`min-w-0 flex-1 text-left ${task.date ? "pr-5" : ""}`}>
          <span className="task-title block">{task.title}</span>
        </button>
        {task.date && (
          <button
            type="button"
            data-testid={`${idPrefix}task-copy-${task.id}`}
            onClick={() => h.copy(task)}
            title="Copia attività"
            aria-label="Copia attività"
            className="absolute -right-1.5 -top-1 flex h-6 w-6 items-center justify-center rounded-md text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
          >
            <CopyPlus className="h-3 w-3" />
          </button>
        )}
      </div>
      <button type="button" data-testid={`${idPrefix}task-details-${task.id}`} onClick={() => h.edit(task)} className="block w-full text-left">
        <Details task={task} p={p} late={late} meta={meta} />
      </button>
      {showAssign && !task.completed && (
        <button type="button" data-testid={`${idPrefix}task-assign-${task.id}`} onClick={() => h.assign(task)} className="assign-btn">
          <UserPlus className="h-3.5 w-3.5" /> {assignLabel}
        </button>
      )}
      {footer}
      {reorder && <ReorderArrows reorder={reorder} testId={`${idPrefix}task-move-${task.id}`} />}
    </div>
  );
};
